import { api, type DefectsOverview, type FleetDefect } from '../api';
import { callout, field, input, opt, openDialog, select, val } from '../dialog';
import { raiseDefect } from '../dialogs/defect';
import { errorCard, esc, fmtDate, html, loading, pill, tile, type State } from '../ui';

/**
 * Defects & MEL.
 *
 * Two questions, answered above everything else: what is stopping an aircraft
 * flying today, and what is about to. A deferral is a decision with an expiry
 * date, so the remaining days are the column that matters — and one that has
 * run out is shown as expired rather than as "deferred", because the table is
 * what somebody checks before they fly.
 */

const SEVERITY_LABEL: Record<string, string> = {
  GROUNDING: 'No flight',
  DEFERRABLE: 'Deferrable',
  ADVISORY: 'Advisory',
};

function rowState(d: FleetDefect): State {
  if (d.severity === 'GROUNDING' || d.expired) return 'OVERDUE';
  if (d.daysLeft !== null && d.daysLeft <= 14) return 'DUE_SOON';
  return d.severity === 'ADVISORY' ? 'NA' : 'OK';
}

function limitText(d: FleetDefect): string {
  if (d.severity === 'GROUNDING') return 'rectify before flight';
  if (d.deferralLimitAt === null) return d.melCategory === 'A' ? 'per the MEL entry' : 'no limit set';
  if (d.expired) return `expired ${fmtDate(d.deferralLimitAt)}`;
  return `${d.daysLeft} d left · ${fmtDate(d.deferralLimitAt)}`;
}

function view(o: DefectsOverview, filter: string): string {
  const shown = o.defects.filter((d) =>
    filter === 'grounding'
      ? d.severity === 'GROUNDING' || d.expired
      : filter === 'deferred'
        ? d.status === 'DEFERRED' && !d.expired
        : true,
  );

  return html`
    <div class="page-h">
      <div>
        <h1>Defects &amp; deferrals</h1>
        <p class="dim">Open items across the fleet, with deferral limits and repeat-defect detection</p>
      </div>
      <button class="btn pri" data-raise>Raise defect</button>
    </div>

    <div class="tiles">
      ${tile({
        label: 'Grounding',
        value: o.summary.grounding,
        sub: o.summary.grounding ? 'aircraft cannot fly' : 'none',
        tone: o.summary.grounding ? 'exceed' : 'ok',
      })}
      ${tile({
        label: 'Deferred — limit ≤ 14 d',
        value: o.summary.dueSoon,
        sub: 'running out soon',
        tone: o.summary.dueSoon ? 'caution' : 'ok',
      })}
      ${tile({ label: 'Deferred — long limit', value: o.summary.longLimit, sub: 'inside the MEL interval' })}
      ${tile({
        label: 'Repeat defects',
        value: o.repeats.length,
        sub: o.repeats.length ? `${o.repeats[0].occurrences} in ${o.repeats[0].windowDays} days` : 'none detected',
        tone: o.repeats.length ? 'caution' : 'ok',
      })}
    </div>

    ${o.repeats.length
      ? html`
          <div class="card">
            <div class="card-h">
              <div>
                <h2>The same thing keeps coming back</h2>
                <p class="tiny dim">
                  Grouped by aircraft and ATA chapter over ${o.repeats[0].windowDays} days — a pattern the
                  individual entries do not show
                </p>
              </div>
            </div>
            <div class="rows">
              ${o.repeats
                .map(
                  (r) => html`
                    <div class="row" style="border-left-color:var(--caution)">
                      <div class="bd">
                        <div class="t">
                          ${esc(r.registration)} · ATA ${esc(r.ataChapter)} — ${r.occurrences} occurrences
                        </div>
                        <div class="m">
                          ${esc(r.references.join(', '))}${r.stillOpen ? ` · ${r.stillOpen} still open` : ' · all closed'}
                          <br />${esc(r.sample)}
                        </div>
                      </div>
                      <a class="btn sm" href="/fleet/${esc(r.aircraftId)}/defects">Open aircraft</a>
                    </div>
                  `,
                )
                .join('')}
            </div>
            <div class="card-f">
              A recurring defect is a reliability signal, not a maintenance one — it belongs in the
              programme review rather than another deferral.
            </div>
          </div>
        `
      : ''}

    <div class="card">
      <div class="card-h">
        <h2>Open defects</h2>
        <div style="display:flex;gap:6px">
          ${['all', 'grounding', 'deferred']
            .map(
              (f) =>
                `<button class="btn sm${filter === f ? ' pri' : ''}" data-filter="${f}">${
                  f === 'all' ? 'All' : f === 'grounding' ? 'Grounding' : 'Deferred'
                }</button>`,
            )
            .join('')}
        </div>
      </div>
      <div class="scrollx">
        <table class="tbl">
          <thead>
            <tr>
              <th>Aircraft</th><th>Reference</th><th class="wide">Defect</th>
              <th class="col-opt">Raised</th><th>Category</th><th>Limit</th><th></th>
            </tr>
          </thead>
          <tbody>
            ${
              shown.length
                ? shown
                    .map(
                      (d) => html`<tr>
                        <td class="st s-${rowState(d) === 'OVERDUE' ? 'exceed' : rowState(d) === 'DUE_SOON' ? 'caution' : 'ok'} n">
                          <a href="/fleet/${esc(d.aircraftId)}/defects" class="reg">${esc(d.registration)}</a>
                        </td>
                        <td class="n">${esc(d.reference)}</td>
                        <td class="wide">
                          ${esc(d.description)}
                          ${d.operationalLimitation
                            ? `<span class="sub2">${esc(d.operationalLimitation)}</span>`
                            : ''}
                          ${d.scheduledOn
                            ? `<span class="sub2">scheduled on ${esc(d.scheduledOn.reference)}</span>`
                            : ''}
                        </td>
                        <td class="col-opt tiny muted">${esc(fmtDate(d.raisedAt))}</td>
                        <td>
                          ${d.melCategory ? `<span class="chip">MEL ${esc(d.melCategory)}</span>` : `<span class="chip">${esc(SEVERITY_LABEL[d.severity] ?? d.severity)}</span>`}
                          ${d.ataChapter ? `<span class="sub2">ATA ${esc(d.ataChapter)}</span>` : ''}
                        </td>
                        <td>
                          ${pill(rowState(d), d.expired ? 'Expired' : SEVERITY_LABEL[d.severity] ?? d.severity)}
                          <span class="sub2">${esc(limitText(d))}</span>
                        </td>
                        <td style="white-space:nowrap">
                          ${d.severity !== 'GROUNDING' && d.status === 'OPEN'
                            ? `<button class="btn sm" data-defer="${esc(d.id)}">Defer</button>`
                            : ''}
                          <button class="btn sm" data-cancel="${esc(d.id)}">Withdraw</button>
                        </td>
                      </tr>`,
                    )
                    .join('')
                : '<tr><td colspan="7" class="muted">Nothing open on this filter.</td></tr>'
            }
          </tbody>
        </table>
      </div>
      <div class="card-f">
        A deferral that reaches its limit becomes a grounding item on its own and appears on the dashboard.
        Rectifying a defect is maintenance, so it goes through a work pack and ends in a release to service —
        it is not closed from this screen.
      </div>
    </div>
  `;
}

// ── dialogs ─────────────────────────────────────────────────────────────────

function deferDefect(o: DefectsOverview, defect: FleetDefect, reload: () => void): void {
  let category = defect.melCategory ?? 'C';

  openDialog<Record<string, unknown>>({
    title: 'Defer under the MEL',
    sub: `${defect.reference} — ${defect.description.slice(0, 60)}`,
    primary: 'Defer',
    body: () => {
      const chosen = o.melCategories.find((c) => c.category === category);
      return html`
        <div class="frow">
          ${field('Category', select('melCategory', o.melCategories.map((c) => opt(c.category, c.label, c.category === category)).join(''), 'data-category'))}
          ${field('MEL reference', input('melReference', defect.melReference ?? '', 'style="font-family:var(--mono)" maxlength="40"'))}
        </div>
        ${field('Operational limitation', input('operationalLimitation', defect.operationalLimitation ?? '', 'placeholder="Day VFR only" maxlength="400"'))}
        ${callout('info', 'i',
          chosen?.days
            ? html`Rectification falls due <b>${chosen.days} days</b> from today. The limit is set by the
                category, not typed in — when it runs out the defect becomes a grounding item on its own.`
            : html`Category A has no fixed interval: the MEL entry itself states the condition, so record it
                in the operational limitation.`,
        )}
      `;
    },
    onRender: (root, refresh) => {
      root.querySelector<HTMLSelectElement>('[data-category]')?.addEventListener('change', (e) => {
        category = (e.target as HTMLSelectElement).value;
        refresh();
      });
    },
    collect: (root) => ({
      melCategory: val(root, 'melCategory'),
      melReference: val(root, 'melReference') || undefined,
      operationalLimitation: val(root, 'operationalLimitation') || undefined,
    }),
    submit: (payload) => api.defects.defer(defect.id, payload),
    done: (r) =>
      r.deferralLimitAt
        ? `${r.reference} deferred — rectify by ${String(r.deferralLimitAt).slice(0, 10)}`
        : `${r.reference} deferred`,
    onDone: reload,
  });
}

function cancelDefect(defect: FleetDefect, reload: () => void): void {
  openDialog<Record<string, unknown>>({
    title: 'Withdraw defect',
    sub: `${defect.reference} — ${defect.description.slice(0, 60)}`,
    primary: 'Withdraw',
    body: () => html`
      ${field('Why it is being withdrawn', input('reason', '', 'placeholder="Raised in error — no fault found on inspection" maxlength="400"'))}
      ${callout('caution', '!', html`
        Withdrawing records that the defect should not have been raised. It is <b>not</b> the same as
        rectifying one: a defect that was real and has been fixed is closed through a work pack, so the
        release to service is recorded against it.
      `)}
    `,
    collect: (root) => {
      const reason = val(root, 'reason');
      if (!reason) return { error: 'Record why it is being withdrawn — the entry stays on the register.' };
      return { reason };
    },
    submit: (payload) => api.defects.cancel(defect.id, payload),
    done: (r) => `${r.reference} withdrawn`,
    onDone: reload,
  });
}

// ── entry point ─────────────────────────────────────────────────────────────

let filter = 'all';

export async function renderDefects(host: HTMLElement): Promise<void> {
  host.innerHTML = loading('Loading defects');
  try {
    const o = await api.defects.overview();
    host.innerHTML = view(o, filter);

    const reload = () => void renderDefects(host);

    host.querySelector('[data-raise]')?.addEventListener('click', () => raiseDefect(o, null, reload));

    host.querySelectorAll<HTMLElement>('[data-filter]').forEach((b) =>
      b.addEventListener('click', () => {
        filter = b.dataset.filter!;
        host.innerHTML = view(o, filter);
        bind(host, o, reload);
      }),
    );

    bind(host, o, reload);
  } catch (err) {
    host.innerHTML = errorCard(err instanceof Error ? err.message : 'Could not load defects.');
  }
}

/** Row actions, rebound after a filter re-render. */
function bind(host: HTMLElement, o: DefectsOverview, reload: () => void): void {
  host.querySelector('[data-raise]')?.addEventListener('click', () => raiseDefect(o, null, reload));
  host.querySelectorAll<HTMLElement>('[data-filter]').forEach((b) =>
    b.addEventListener('click', () => {
      filter = b.dataset.filter!;
      host.innerHTML = view(o, filter);
      bind(host, o, reload);
    }),
  );
  host.querySelectorAll<HTMLElement>('[data-defer]').forEach((b) =>
    b.addEventListener('click', () => {
      const d = o.defects.find((x) => x.id === b.dataset.defer);
      if (d) deferDefect(o, d, reload);
    }),
  );
  host.querySelectorAll<HTMLElement>('[data-cancel]').forEach((b) =>
    b.addEventListener('click', () => {
      const d = o.defects.find((x) => x.id === b.dataset.cancel);
      if (d) cancelDefect(d, reload);
    }),
  );
}
